import type { FC } from 'react';
import { memo, Suspense, useCallback } from 'react';
import Box from '@mui/material/Box';
import { CircularProgress, Skeleton, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import { Add, Cancel, CheckCircle, ChevronRightOutlined, Lock as LockIcon } from '@mui/icons-material';
import { useAdsHistory } from '../api';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import type { FallbackProps } from 'react-error-boundary';
import Stack from '@mui/material/Stack';
import { ButtonStyled, GetNewUniqueId, OverviewContainer, OverviewEntryContainer } from 'features/cabinet/dashboard';
import { useNavigate } from 'react-router';
import { usePermissionGuard } from 'features/login';

const ActivityErrorBoundaryFallback: FC<FallbackProps> = () => {
  return (
    <>
      <OverviewEntryContainer sx={{ px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color={'error'} />
          <Typography variant={'body1'} color={'error'}>
            An unexpected error happened.
          </Typography>
        </Box>
      </OverviewEntryContainer>
      <OverviewEntryContainer sx={{ px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color={'error'} />
          <Typography variant={'body1'} color={'error'}>
            An unexpected error happened.
          </Typography>
        </Box>
      </OverviewEntryContainer>
    </>
  );
};

const ActivitySkeleton: FC = () => {
  return (
    <>
      <OverviewEntryContainer>
        <Skeleton
          width={'100%'}
          height={'100%'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
      </OverviewEntryContainer>
      <OverviewEntryContainer>
        <Skeleton
          width={'100%'}
          height={'100%'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
      </OverviewEntryContainer>
    </>
  );
};

const ActivityEntry = (
  {
    status,
    Icon,
    color,
    title,
  }: {
    status: boolean;
    Icon: SvgIconComponent;
    color: 'error' | 'success';
    title: string;
  }) => {
  const lastTwoDays = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];
  const { data } = useAdsHistory({
    params: {
      status,
      limit: 0,
      offset: 0,
      start_date: lastTwoDays,
    },
  });
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    navigate(
      `/cabinet/facebook/ads-history?page=1&status=${status ? 'success' : 'failed'}&date=${lastTwoDays}`,
    );
  }, [lastTwoDays, navigate, status]);

  return (
    <OverviewEntryContainer>
      <ButtonStyled variant={'text'} size={'small'} onClick={handleClick}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon color={color} />
            <Typography sx={(t) => ({ fontSize: t.typography.body1.fontSize })}>
              {data.count} ads
            </Typography>
          </Box>
          <Typography
            sx={(t) => ({
              fontSize: t.typography.caption.fontSize,
              color: t.palette[color].main,
            })}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            gap: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          <Typography
            sx={(t) => ({
              textTransform: 'uppercase',
              fontSize: t.typography.button.fontSize,
              fontWeight: 500,
            })}
          >
            View details
          </Typography>
          <ChevronRightOutlined />
        </Box>
      </ButtonStyled>
    </OverviewEntryContainer>
  );
};

const LastActivitiesComponent = memo(() => {
  const { accessToViewAdlog } = usePermissionGuard();

  return accessToViewAdlog ? (
    <>
      <ActivityEntry
        status={false}
        Icon={Cancel}
        color="error"
        title="Failed to create"
      />
      <ActivityEntry
        status={true}
        Icon={CheckCircle}
        color="success"
        title="Successfully posted"
      />
    </>
  ) : (
    <>
      <OverviewEntryContainer>
        <ButtonStyled
          disabled
          variant={'text'}
          size={'small'}
          onClick={() => null}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Typography sx={(t) => ({ fontSize: t.typography.body2.fontSize })}>
            No access to Ads history
          </Typography>
        </ButtonStyled>
      </OverviewEntryContainer>
      <OverviewEntryContainer>
        <ButtonStyled
          disabled
          variant={'text'}
          size={'small'}
          onClick={() => null}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Typography sx={(t) => ({ fontSize: t.typography.body2.fontSize })}>
            No access to Ads history
          </Typography>
        </ButtonStyled>
      </OverviewEntryContainer>
    </>
  );
});

LastActivitiesComponent.displayName = 'LastActivitiesComponent';

export const LastActivities = memo(() => {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
    >
      <Typography sx={(t) => ({ fontSize: t.typography.body2.fontSize })}>
        Last activity in Facebook (2 days)
      </Typography>
      <OverviewContainer>
        <GlobalErrorBoundary FallbackComponent={ActivityErrorBoundaryFallback}>
          <Suspense fallback={<ActivitySkeleton />}>
            <LastActivitiesComponent />
          </Suspense>
        </GlobalErrorBoundary>
      </OverviewContainer>
    </Box>
  );
});

LastActivities.displayName = 'LastActivities';

export const ActionEntryButton = memo(
  ({
     title,
     Icon,
     onClick,
     disabled,
     loading,
     hintTitle,
   }: {
    title: string;
    Icon: SvgIconComponent;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    hintTitle?: string;
  }) => (
    <ButtonStyled
      variant={'text'}
      size={'small'}
      onClick={onClick}
      disabled={disabled}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'start',
          gap: disabled ? 1.5 : 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:
              (loading ?? disabled)
                ? 'transparent'
                : 'rgba(41, 121, 255, 0.04)',
          }}
        >
          {loading ? (
            <CircularProgress size={28} color="primary" />
          ) : (
            <Icon
              color={'primary'}
              sx={(t) => ({
                fontSize: disabled ? 32 : 28,
                color: disabled ? t.palette.action.disabled : 'primary',
              })}
            />
          )}
        </Box>
        <Typography
          variant={'body2'}
          textTransform={disabled ? 'initial' : 'uppercase'}
          sx={{ textAlign: 'left' }}
          fontWeight={disabled ? 400 : 500}
        >
          {title}
          <Typography component={'span'} variant={'body2'} color={'gray'}
                      sx={{ textTransform: 'none', pl: 1 / 2 }}>{hintTitle}</Typography>
        </Typography>
      </Stack>
    </ButtonStyled>
  ),
);

ActionEntryButton.displayName = 'ActionEntryButton';

export const Actions = memo(() => {
  const navigate = useNavigate();
  const { accessToAddFlow, accessToAddOriginaltextcreative } =
    usePermissionGuard();

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
    >
      <Typography sx={(t) => ({ fontSize: t.typography.body2.fontSize })}>
        Actions
      </Typography>
      <OverviewContainer>
        {accessToAddFlow ? (
          <OverviewEntryContainer>
            <ActionEntryButton
              Icon={Add}
              title={'Add facebook flow '}
              onClick={() => {
                navigate('/cabinet/facebook/flows/add');
              }}
            />
          </OverviewEntryContainer>
        ) : (
          <OverviewEntryContainer>
            <ActionEntryButton
              disabled
              Icon={LockIcon}
              title={'No access to add flow'}
              onClick={() => null}
            />
          </OverviewEntryContainer>
        )}
        {accessToAddOriginaltextcreative ? (
          <OverviewEntryContainer>
            <ActionEntryButton
              Icon={Add}
              title={'Add facebook text'}
              onClick={() => {
                navigate('/cabinet/facebook/creative-texts/add');
              }}
            />
          </OverviewEntryContainer>
        ) : (
          <OverviewEntryContainer>
            <ActionEntryButton
              disabled
              Icon={LockIcon}
              title={'No access to add text'}
              onClick={() => null}
            />
          </OverviewEntryContainer>
        )}
        <OverviewEntryContainer>
          <GetNewUniqueId />
        </OverviewEntryContainer>
      </OverviewContainer>
    </Box>
  );
});

Actions.displayName = 'Actions';
